import qz from 'qz-tray';

import { PrinterNotFoundError } from './printer.types';
import axios from '@/core/axios';
import { AppExternalError } from '@/core/errors/app-external-error.class';

// Expose qz globally for browser compatibility
(window as any).qz = qz;

class PrinterService {
  constructor() {
    qz.security.setCertificatePromise((resolve: any, reject: any) => this.getCertificate().then(resolve).catch(reject));
    qz.security.setSignatureAlgorithm('SHA512'); // Since 2.1
    qz.security.setSignaturePromise((certificate: string) => {
      return (resolve: any, reject: any) => this.signCertificate(certificate).then(resolve).catch(reject);
    });
  }

  printWith(data: any, printerRole: string): Promise<any> {
    return this.assertConnection()
      .then(() => this.resolvePrinter(printerRole))
      .then(printer => {
        const config = qz.configs.create(printer, {
          /* macOS no longer supports raw CUPS queues, so any driver attached to the
           * printer would otherwise reformat/rasterize this raw ZPL as plain text */
          ...(printerRole ? { forceRaw: true } : {}),
          ...(printerRole === 'SHIPPING' ? { size: { width: 15, height: 11 }, unit: 'cm' } : {})
        });
        return qz.print(config, data);
      })
      .catch(error => {
        console.error(error);
        if (error instanceof PrinterNotFoundError) throw error;
        if (error?.message === 'Specified printer could not be found.') throw new PrinterNotFoundError(printerRole);
        else throw new AppExternalError();
      });
  }

  getCertificate(): Promise<string> {
    return axios.post<{ certificate: string }>('/printer/certificate').then(resp => resp?.data?.certificate);
  }

  signCertificate(certificate: string): Promise<string> {
    return axios.post<{ encoded: string }>('/printer/sign', { certificate }).then(resp => resp?.data?.encoded);
  }

  private resolvePrinter(printerRole: string): Promise<any> {
    if (!printerRole) return qz.printers.getDefault();

    return qz.printers.find(printerRole).catch(() => this.autoDetectLabelPrinter(printerRole));
  }

  /* The role name (e.g. "SHIPPING") is not found as-is when this workstation's printer was
   * replaced by a different model (e.g. a ZD220 swapped for a ZD421): automatically fall back
   * to the workstation's only Zebra/ZPL label printer instead of requiring manual reconfiguration */
  private autoDetectLabelPrinter(printerRole: string): Promise<string> {
    return qz.printers.find().then((printers: string[]) => {
      const labelPrinters = printers.filter(name => /zebra|zpl/i.test(name));
      if (labelPrinters.length === 1) return labelPrinters[0];
      throw new PrinterNotFoundError(printerRole);
    });
  }

  /* The secure (wss) handshake fails silently whenever this workstation's OS/browser does not
   * trust QZ Tray's self-signed certificate, even though the certificate itself is valid and QZ
   * Tray is running. Falling back to the insecure (ws) port avoids depending on that trust store
   * being configured on every machine, without weakening security since traffic stays on loopback
   * and print data is still authenticated via the signed certificate exchanged above */
  private assertConnection(): Promise<void> {
    if (qz.websocket.isActive()) return Promise.resolve();

    return qz.websocket.connect().catch(() => qz.websocket.connect({ usingSecure: false }));
  }
}

export const printerService = new PrinterService();
